import type { FC } from "react"
import React from "react"

import type { RoofEdgeType } from "@logic/lib/design"
import { ROOF_EDGE_TYPES } from "@logic/lib/design"
import { useTranslation } from "react-i18next"
import { useRecoilState, atom, useRecoilValue } from "recoil"
import styled from "styled-components"

import { designLocalState } from "~/containers/Proposal/state/designs/design-state"
import {
  currentUserIsDesignRequestUserState,
  userHasRequiredPermissionsState,
} from "~/containers/Proposal/state/user/user-state"
import { useTriggerPreventionToast } from "~/hooks/useTriggerPreventionToast"
import type { Interlingual } from "~/i18n/types"
import { fontWeight } from "~/utils/styles"

import { ReactComponent as Gear } from "@svg/gear.svg"

import { SERVICE_BUREAU_PHASE_ALLOWED_PERMISSIONS } from "../state/phase"

const SettingsWrapper = styled.div`
  position: relative;
  > button {
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    :disabled,
    &.blocked {
      path {
        stroke: var(--grey-dark);
      }
    }
    :disabled {
      cursor: not-allowed;
      path {
        stroke: var(--grey-border);
      }
    }
  }
`

const SettingsContainer = styled.div`
  position: absolute;
  top: 30px;
  right: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 300px;
  padding: 20px;
  background: white;
  box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.1);
  border: 1px solid var(--grey-border);
  border-radius: 10px;
  gap: 15px;

  h3 {
    text-transform: capitalize;
    font-size: var(--step--1);
    line-height: 17px;
    font-weight: 600;
    margin-bottom: 10px;
  }
`

const SetbackSpan = styled.span`
  display: flex;
  * {
    font-size: var(--step--1);
  }
  ~ span {
    margin-top: 5px;
    padding-top: 5px;
    border-top: 1px solid var(--grey-border);
  }
  label {
    width: 100%;
    flex-grow: 1;
  }
  input[type="number"] {
    flex-grow: 0;
    width: 70px;
    text-align: right;
  }
  p {
    width: 20px;
    flex-grow: 0;
  }
`

const RoofTypeInterlingual: Record<RoofEdgeType, Interlingual> = {
  eave: `EdgeType_Eave`,
  hip: `EdgeType_Hip`,
  rake: `EdgeType_Rake`,
  ridge: `EdgeType_Ridge`,
  valley: `EdgeType_Valley`,
}

export const SetbackEditor: FC<{ type: RoofEdgeType }> = ({ type }) => {
  const { t } = useTranslation()
  const typeInterlingual = RoofTypeInterlingual[type]
  const [design, setDesign] = useRecoilState(designLocalState)

  const changeSetback = (setback: number) =>
    setDesign((current) => ({
      ...current,
      setbacks: { ...current.setbacks, [type]: setback },
    }))

  return (
    <SetbackSpan>
      <label htmlFor={`setback-${type}`}>
        <>{t(typeInterlingual)}</>
      </label>
      <input
        type="number"
        value={design.setbacks[type]}
        onChange={(event) => changeSetback(Number(event.target.value))}
      />
      <p>in</p>
    </SetbackSpan>
  )
}

const Settings_Internal: FC = () => {
  const [settingsIsOpen, setSettingsIsOpen] = React.useState(false)
  const hasRequiredPermissions = useRecoilValue(
    userHasRequiredPermissionsState(SERVICE_BUREAU_PHASE_ALLOWED_PERMISSIONS),
  )
  const currentUserIsDesignUser = useRecoilValue(
    currentUserIsDesignRequestUserState,
  )
  const triggerPreventionToast = useTriggerPreventionToast()

  const clickSettingsIcon = () => {
    if (!currentUserIsDesignUser) {
      triggerPreventionToast()

      return
    }
    setSettingsIsOpen((current) => !current)
  }

  return (
    <SettingsWrapper>
      <button
        className={!currentUserIsDesignUser ? `blocked` : ``}
        onClick={clickSettingsIcon}
        disabled={!hasRequiredPermissions}
        title="Settings"
      >
        <Gear />
      </button>
      {settingsIsOpen && (
        <SettingsContainer>
          <div>
            <h3>Setbacks</h3>
            {ROOF_EDGE_TYPES.map((type) => (
              <SetbackEditor key={type} type={type} />
            ))}
          </div>
        </SettingsContainer>
      )}
    </SettingsWrapper>
  )
}
export const Settings = React.memo(Settings_Internal)
